import FramedIcon from '@passkeys/ui/framed-icon';
import type { IconName } from '@passkeys/ui/icon';
import { type CSSProperties } from 'react';

import { cn } from '@/ui/utils/classnames';

const VARIANT_TO_ICON_NAME = {
  horizontal: 'arrow-left-arrow-right',
  vertical: 'arrow-up-arrow-down',
} as const satisfies { [variant: string]: IconName };

type FlipButtonVariant = keyof typeof VARIANT_TO_ICON_NAME;

export interface FlipButtonProps {
  variant: FlipButtonVariant;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  onPress?: () => void;
}

function FlipButton({
  variant,
  disabled,
  className,
  style,
  onPress,
}: FlipButtonProps) {
  return (
    <div
      className={cn(
        'relative z-10',
        variant === 'vertical' &&
          'h-px w-full bg-black/6 theme-dark:bg-neutral-600',
        variant === 'horizontal' && 'h-full w-px',
        className,
      )}
    >
      <button
        className={cn(
          'size-10 text-gray-500 theme-dark:text-neutral-200',
          'absolute inset-1/2 translate-x-[-50%] translate-y-[-50%]',
        )}
        disabled={disabled}
        onClick={onPress}
        type="button"
        style={style}
        data-testid="flip-button"
      >
        <FramedIcon name={VARIANT_TO_ICON_NAME[variant]} size={40} />
      </button>
    </div>
  );
}

export const VerticalFlipButton = (props: Omit<FlipButtonProps, 'variant'>) => (
  <FlipButton {...props} variant="vertical" />
);
export const DisabledVerticalFlipButton = (
  props: Omit<FlipButtonProps, 'disabled' | 'variant'>,
) => <FlipButton {...props} variant="vertical" disabled />;
export const HorizontalFlipButton = (
  props: Omit<FlipButtonProps, 'variant'>,
) => <FlipButton {...props} variant="horizontal" />;
export const DisabledHorizontalFlipButton = (
  props: Omit<FlipButtonProps, 'disabled' | 'variant'>,
) => <FlipButton {...props} variant="horizontal" disabled />;

export default FlipButton;
